import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import Layout from "../../layouts/Layout";

const AboutUs = () => {
  return (
    <Layout>
      <Box
        sx={{
          px: { xs: 2, md: 8 },
          py: { xs: 4, md: 8 },
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Handpik.ai — Smart AI Search for Real-Life Moments
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", maxWidth: "700px", mx: "auto" }}
          >
            It began with something small: finding an outfit for an important
            event. Five minutes became half a day—tab after tab, endless product
            grids, filters that missed the brief.
          </Typography>
        </Box>

        {/* Story Section */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          We don’t shop by menus — we shop by moments and moods.
        </Typography>
        <Box component="ul" sx={{ mb: 5 }}>
          {[
            "“Mehndi look that stands out on camera, sleeves past the elbow—plus a matching bag.”",
            "“Beach-friendly nikah fit—light fabric, not see-through—sandals to match.”",
            "“Office-ready set: modest cut, breathable fabric, minimalist watch.”",
            "“Weekend brunch: smart-casual outfit, crossbody bag, clean sneakers.”",
            "“Travel capsule: heat-friendly layers, wrinkle-resistant pieces, compact tote.”",
            "“Gift run: kids’ toys that delight, not clutter.”",
          ].map((item, i) => (
            <Typography
              key={i}
              component="li"
              sx={{ mb: 1, color: "text.secondary" }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* What We Do */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          What Handpik.ai Does
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
          Your AI shopping co-pilot that understands intent and speaks your
          language—so landing on your vibe feels effortless.
        </Typography>
        <Box component="ul" sx={{ mb: 5 }}>
          {[
            "Conversational search: describe your moment, mood, fit preferences (modesty, sleeve length, hemlines, fabric) — get spot-on picks in seconds.",
            "Visual search: upload an inspo screenshot; we find look-alikes and build the whole set around it.",
            "Cross-store comparison: explore options and prices from 85+ trusted retailers in one view.",
            "Price tracking & alerts: get nudged when the perfect item goes on sale.",
            "Smart alternatives: discover similar cuts, colors, and accessories across the web.",
            "Save & share: curate wishlists for weddings, travel, or everyday fits — share with friends or family.",
            "Made for our region: modest options, heat-friendly materials, and clear size conversions that help.",
          ].map((item, i) => (
            <Typography
              key={i}
              component="li"
              sx={{ mb: 1, color: "text.secondary" }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* What Makes Our AI Smart */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          What Makes Our AI “Smart”
        </Typography>
        <Box component="ul" sx={{ mb: 5 }}>
          {[
            "Understands real intent: “flowy, ankle-length, sleeves past the elbow” beats “dress > maxi > blue.”",
            "Reasoned ranking: blends fit, fabric, reviews, and availability to prioritize what you’ll actually wear.",
            "Multi-modal matching: combines text + images to assemble full looks — outfit, bag, shoes, watch, accessories.",
            "Transparent choices: we explain why items appear, so decisions feel confident.",
            "Human in the loop: editors review AI results for cultural fit and quality.",
          ].map((item, i) => (
            <Typography
              key={i}
              component="li"
              sx={{ mb: 1, color: "text.secondary" }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* Momentum Section */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Momentum So Far
        </Typography>
        <Grid container spacing={2} mb={4}>
          {[
            { label: "Monthly Active Users", value: "25k+" },
            { label: "Partner Retailers", value: "85+" },
            { label: "Products Indexed", value: "1M+" },
            { label: "User Satisfaction", value: "95%" },
          ].map((item, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card
                elevation={0}
                sx={{
                  backgroundColor: "#FFF6F4",
                  textAlign: "center",
                  borderRadius: "16px",
                  py: 3,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#FFB79C" }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {item.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          We started with a private beta in December, opened publicly in May,
          and we expand our catalog and capabilities every week.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* Team Section */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Who’s Building Handpik.ai
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          A tight team of engineers, data scientists, and product folks who’ve
          shipped large-scale search, recommendation systems, and AI products.
          Now we’re fixing what’s broken in fashion e-commerce for shoppers
          across Pakistan and the Middle East.
        </Typography>

        {/* Closing Line */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mt: 6,
            textAlign: "center",
            color: "#FFB79C",
          }}
        >
          At its core, Handpik.ai removes friction — less scrolling, fewer tabs,
          smarter choices.
          <br />A little tadka of Smart AI Search — so you find your vibe, head
          to toe.
        </Typography>
      </Box>
    </Layout>
  );
};

export default AboutUs;
